// The shell.
//
// Everything between the pure simulation and a human being: a canvas, a
// renderer, one loop, the input translation, the HUD and the results
// screen. It is the only file in `lib/daemon` that knows React exists,
// and it is deliberately host-agnostic — nothing here imports from
// `src/shell/` or `src/lib/wm`, because the same component has to render
// inside a skenOS window and as the whole page at daemon.skenmy.com.
// Everything that differs goes through `DaemonHost`.
//
// Three things here are load-bearing and easy to get wrong:
//
//   • **Coordinates.** The sim thinks in a 1000x1000 logical arena; the
//     canvas is whatever size the window happens to be. The renderer
//     letterboxes; this file computes the *inverse* of that letterbox
//     itself rather than asking the renderer, so the two never end up
//     coupled through a shared mutable transform.
//
//   • **Pointer lock.** The cursor is the hitbox, so a cursor that
//     wanders out of the window is a dead player who never saw it
//     coming. We lock it, integrate `movementX/Y` into a virtual
//     cursor, and the moment the lock drops — Escape, alt-tab, a
//     browser deciding it has had enough — we pause and say so in words
//     on screen. The failure mode we refuse to ship is "nothing
//     responds and there is no explanation".
//
//   • **Restart.** One key, no menu, under 300ms. `createRun` is cheap
//     and the renderer is never rebuilt, so restarting is a fresh state
//     object and a fresh loop; the canvas, its context and every cached
//     gradient survive untouched.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from "react";

import "./daemon.css";
import { createLoop, type Loop } from "./loop";
import { rngFromSeed } from "./rng";
import { createVoice, OPENING_SCRIPT, type Voice, type VoiceContext } from "./voice";
import { DAEMON_LINES } from "../../content/daemon-lines";
import { createRecorder, encodeReplay, type Recorder } from "./replay";
import { submitRun, type SubmitResult } from "./leaderboard";
import { createTelemetry, trackDraft, trackRestart, type Telemetry } from "./telemetry";
import { computeTransform, createRenderer, type ViewTransform } from "./render";
import {
  createRun,
  formatTicks,
  resolveDraft,
  signalRefused,
  signalsLocked,
  SIGNALS,
} from "./sim";
import { LeaderboardPanel } from "./LeaderboardPanel";
import { UpgradeCodex } from "./UpgradeCodex";
import {
  activeLinks,
  describeOffers,
  PICKS_PER_RUN,
  upgradeById,
} from "./upgrades";
import { phaseAt } from "./phases";
import {
  categoryById,
  emptyRecords,
  formatDelta,
  pbKey,
  sumOfBest,
  type CategoryId,
  type PersonalBest,
  type Records,
} from "./records";
import {
  buildRows,
  commitRun,
  createSplitTracker,
  loadSplitPref,
  planSplitKeys,
  readRecords,
  referenceAt,
  RECORDS_SUFFIX,
  safeStorage,
  saveSplitPref,
  SPLITS_PREF_SUFFIX,
  type RunRecord,
  type SplitRow,
  type SplitTracker,
} from "./splits";
import type { DaemonHost } from "./host";
import {
  ARENA_H,
  ARENA_W,
  EMPTY_INPUT,
  TICK_HZ,
  type RunConfig,
  type NiceLevel,
  type SignalId,
  type SimEvent,
  type SimInput,
  type SimState,
} from "./types";

// ── Tuning ────────────────────────────────────────────────────────────

/**
 * How long the results screen ignores a restart key, in ms.
 *
 * Long enough to swallow a keystroke that was already travelling when
 * the run ended, short enough that nobody deliberately pressing R will
 * notice. Not a confirmation dialog — those get dismissed reflexively
 * too, and the point is only to protect the submit form for the half
 * second it takes to register that the run is over.
 *
 * Wall-clock rather than ticks on purpose: the simulation has stopped
 * by the time this matters, so there is no tick to count, and nothing
 * about it is recorded or replayed.
 */
const RESULTS_SETTLE_MS = 600;

/** How long the "card cleared" banner stays up, in ms. Long enough to
 *  read three words, short enough to be gone before the next pattern
 *  needs the space. */
const CARD_CLEAR_BANNER_MS = 1300;

/** The difficulty ladder, kindest first. Mirrors `NICE_LEVELS` in
 *  daily.ts and daemon-main.tsx; the real home is `types.ts` next to
 *  `NiceLevel`. */
const NICE_CHOICES: NiceLevel[] = [19, 10, 5, 0, -5, -10, -15, -20];

/** What each rung actually costs you. `nice` is a real Unix idea — a
 *  high nice value means a process is being *considerate* — so the
 *  scale runs backwards from what a difficulty slider usually does, and
 *  saying so is cheaper than letting someone find out. */
const NICE_BLURB: Record<number, string> = {
  19: "maximally considerate. DAEMON is barely trying. Start here.",
  10: "polite. Room to learn a pattern before it kills you.",
  5: "mild. The default if you have played a bullet hell before.",
  0: "the priority an ordinary process runs at. The honest normal.",
  [-5]: "elevated. Faster patterns, less forgiveness.",
  [-10]: "aggressive. Assumes you know the cards.",
  [-15]: "hostile. Assumes you have cleared them.",
  [-20]: "maximum priority. It is not considerate at all now.",
};

/**
 * Ticks between auto-repeats while the fire button is held.
 *
 * `SimInput.fire` is an edge — true on the tick the button went down —
 * so holding the button would otherwise fire once and stop. SIGTERM's
 * own cooldown is 9 ticks, so re-arming on that cadence is exactly
 * "hold to fire at the maximum legal rate" and never queues a press the
 * sim would reject. Raise it to make holding worse than clicking; there
 * is no reason to.
 */
const AUTOFIRE_TICKS = 9;

/** Mouse gain while pointer-locked. 1 = identical to the unlocked feel,
 *  which is the only defensible default when the cursor *is* the ship. */
const SENSITIVITY = 1;

/** Screenshake impulse per event, in CSS px. Suppressed entirely under
 *  `host.reducedMotion()`. */
const SHAKE_HIT = 13;
const SHAKE_CRIT = 6;
/** Multiplied into the shake amplitude each frame. 0.86 ≈ 140ms decay. */
const SHAKE_DECAY = 0.86;

/** Digit keys, by physical position so a non-QWERTY layout still works.
 *  Slot 0 (SIGTERM) is the mouse button, so the row starts at SIGSTOP. */
const SIGNAL_KEYS: Record<string, SignalId> = {
  Digit1: "sigstop",
  Digit2: "sigkill",
  Digit3: "renice",
  Digit4: "strace",
  Numpad1: "sigstop",
  Numpad2: "sigkill",
  Numpad3: "renice",
  Numpad4: "strace",
};
/**
 * The category the live panel races against.
 *
 * any%, always. The other three are *derived* — you do not declare a
 * no-hit attempt, you either got hit or you didn't — so racing one of
 * them live would mean the comparison silently changed the moment a
 * bullet landed. any% is the only category that is true for the whole
 * run, which makes it the only one a live delta can mean anything
 * against.
 */
const LIVE_CATEGORY: CategoryId = "any";

/** Ticks between live-segment clock writes. 6 = a tenth of a second,
 *  which is the finest a human reads a running delta at anyway, and it
 *  turns a 60Hz text write into a 10Hz one. Zero React renders either
 *  way — the value goes straight to the text node, like the IGT clock. */
const LIVE_QUANTUM = 6;

/** Draft picks reuse the same physical row as the signals. */
const DRAFT_KEYS: Record<string, number> = {
  Digit1: 0,
  Digit2: 1,
  Digit3: 2,
  Numpad1: 0,
  Numpad2: 1,
  Numpad3: 2,
};

const SIGNAL_KEY_HINT: Record<SignalId, string> = {
  sigterm: "LMB",
  sigstop: "1",
  sigkill: "2",
  renice: "3",
  strace: "4",
};

// ── Small view models ─────────────────────────────────────────────────

/** The parts of the HUD that change rarely enough to be React state.
 *  The run timer is *not* in here — it changes every tick and is written
 *  straight to its text node, so a 60Hz clock costs zero reconciliation. */
type Hud = {
  daemonHp: number;
  daemonHpMax: number;
  guarded: boolean;
  playerHp: number;
  playerHpMax: number;
  priv: number;
  privMax: number;
  /** Cooldown remaining as a 0-1 fraction, quantised to 20 buckets so a
   *  ticking cooldown re-renders 20 times a second, not 60. */
  cooldowns: number[];
  phaseIndex: number;
  cardName: string;
  /** Whole seconds left on the card. One render per second. */
  cardSeconds: number;
  /** Runlevel 1 takes every verb away. The HUD has to *say* so, or a
   *  row of dead keys reads as a bug instead of as the phase's point. */
  locked: boolean;
  /** Signal ids the current phase refuses. Single-user takes everything
   *  except SIGKILL, so this is no longer all-or-nothing. */
  refused: SignalId[];
  /** Draft offers, ids. Empty unless the run is sitting on a draft. */
  offers: string[];
  upgrades: string[];
  /** Points so far. */
  score: number;
  /** Live load average in thousandths, quantised to hundredths for the
   *  HUD so the readout changes ten times a second rather than sixty. */
  loadCenti: number;
};

type Results = {
  won: boolean;
  ticks: number;
  grazes: number;
  score: number;
  peakLoadMilli: number;
  hitsTaken: number;
  cardsCleared: number;
  damageDealt: number;
  signalsFired: number;
  /** What the logbook made of it. Null when storage was unavailable or
   *  the run had already been committed — the screen degrades to the
   *  plain stat grid rather than pretending to know. */
  record: RunRecord | null;
  /** This run's segments, compared against the PB that existed *before*
   *  it. Computed once, here, so the plate is pure render. */
  rows: SplitRow[];
  /** The PB being compared against, if there was one. */
  pbTicks: number | null;
  /** Sum of best *after* this run — the time still on the table. */
  sob: number | null;
};

const hudFrom = (s: SimState): Hud => {
  const card = s.cardPlan[s.phaseIndex]?.[s.cardIndex];
  return {
    daemonHp: s.daemon.hp,
    daemonHpMax: s.daemon.hpMax,
    guarded: s.daemon.guarded,
    playerHp: s.player.hp,
    playerHpMax: s.player.hpMax,
    priv: s.player.priv,
    privMax: s.player.privMax,
    cooldowns: s.player.cooldowns.map((c, i) => {
      const total = SIGNALS[i]?.cooldown ?? 1;
      return total > 0 ? Math.round((c / total) * 20) / 20 : 0;
    }),
    phaseIndex: s.phaseIndex,
    cardName: card?.name ?? "",
    cardSeconds: Math.max(0, Math.ceil(s.cardTicks / TICK_HZ)),
    locked: signalsLocked(s),
    refused: SIGNALS.filter((sig) => signalRefused(s, sig.id)).map((sig) => sig.id),
    offers: s.offers,
    upgrades: s.upgrades,
    score: s.stats.score,
    loadCenti: Math.round(s.player.loadMilli / 10),
  };
};

const hudEq = (a: Hud, b: Hud): boolean =>
  a.daemonHp === b.daemonHp &&
  a.daemonHpMax === b.daemonHpMax &&
  a.guarded === b.guarded &&
  a.playerHp === b.playerHp &&
  a.playerHpMax === b.playerHpMax &&
  a.priv === b.priv &&
  a.privMax === b.privMax &&
  a.phaseIndex === b.phaseIndex &&
  a.cardName === b.cardName &&
  a.cardSeconds === b.cardSeconds &&
  a.locked === b.locked &&
  a.refused.length === b.refused.length &&
  a.offers === b.offers &&
  a.upgrades === b.upgrades &&
  a.score === b.score &&
  a.loadCenti === b.loadCenti &&
  a.cooldowns.length === b.cooldowns.length &&
  a.cooldowns.every((c, i) => c === b.cooldowns[i]);

/**
 * The letterbox, inverted.
 *
 * The renderer fits the square arena into whatever rectangle it is given
 * and centres the remainder; input has to undo exactly that, or the
 * hitbox sits somewhere other than the cursor and the game is a lie.
 *
 * We compute it from **our own** measurement of the canvas box — the
 * renderer instance is never consulted — but we do it with the same pure
 * `computeTransform` the renderer uses, because `arenaScale` is folded
 * into that scale and a second, subtly different copy of the formula is
 * a cursor-offset bug with a delay fuse. `dpr` is passed as 1: we work
 * entirely in CSS pixels, which is what pointer events report.
 */
function measureView(out: ViewTransform, w: number, h: number, arenaScale: number): ViewTransform {
  return computeTransform(w, h, 1, arenaScale, out);
}

const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

const POINTER_LOCK_SUPPORTED =
  typeof document !== "undefined" &&
  typeof Element !== "undefined" &&
  "requestPointerLock" in Element.prototype;

// ── The component ─────────────────────────────────────────────────────

export function DaemonGame({
  host,
  config,
}: {
  host: DaemonHost;
  config: RunConfig;
}): JSX.Element {
  // A new run is only a new run when the *values* change. Parents that
  // rebuild the config object every render must not restart the fight.
  /**
   * Difficulty chosen in-game, overriding whatever the caller passed.
   *
   * `nice` has eight levels, its own tuning curve and its own board per
   * level — and until now the only way to reach any of them was to
   * hand-edit `?nice=` in the URL. The ladder existed and was
   * unreachable, which made seven eighths of the game's difficulty
   * range invisible to anyone who did not read the source.
   *
   * Null means "whatever the caller said", so a `?nice=` link and the
   * skenOS launcher both still win until the player says otherwise.
   */
  const [nicePick, setNicePick] = useState<NiceLevel | null>(null);
  const activeConfig = useMemo<RunConfig>(
    () => (nicePick === null ? config : { ...config, nice: nicePick }),
    [config, nicePick],
  );

  // Changing this rebuilds the run from scratch — which is exactly what
  // picking a difficulty should do, and is why the picker is only
  // offered before a run has started.
  const configKey = `${activeConfig.seed}|${activeConfig.nice}|${activeConfig.modifier?.id ?? "-"}|${activeConfig.daily}`;
  const configRef = useRef(activeConfig);
  configRef.current = activeConfig;
  const hostRef = useRef(host);
  hostRef.current = host;

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const timerRef = useRef<HTMLSpanElement | null>(null);
  const rendererRef = useRef<ReturnType<typeof createRenderer> | null>(null);
  const loopRef = useRef<Loop | null>(null);
  const stateRef = useRef<SimState | null>(null);
  const viewRef = useRef<ViewTransform>(computeTransform(ARENA_W, ARENA_H, 1, 1));

  // Input, all in refs: it is sampled from inside the loop, and none of
  // it should ever cause a render.
  const cursorRef = useRef({ x: EMPTY_INPUT.x, y: EMPTY_INPUT.y });
  const fireArmedRef = useRef(false);
  const holdingRef = useRef(false);
  const tickCountRef = useRef(0);
  const lastFireTickRef = useRef(-AUTOFIRE_TICKS);
  const pendingSignalsRef = useRef<SignalId[]>([]);

  const shakeRef = useRef(0);
  const flashRef = useRef(0);
  const lastScaleRef = useRef(1);
  const voiceRef = useRef<Voice | null>(null);
  /** Every run is recorded, not just good ones — that is what makes
   *  attract mode and ghost racing possible at all. */
  const recorderRef = useRef<Recorder | null>(null);
  const telemetryRef = useRef<Telemetry | null>(null);

  // ── Records ─────────────────────────────────────────────────────────
  // All of it in refs, all of it read-only with respect to the sim. The
  // one thing that must stay true: a run plays identically whether any
  // of this works or not.
  /** Per-run watcher. Rebuilt on every fresh run, never on a draft. */
  const splitsRef = useRef<SplitTracker | null>(null);
  /** The book, as loaded at run start and updated on commit. */
  const recordsRef = useRef<Records>(emptyRecords());
  /** Golds and PB splits, snapshotted at run start. Comparing against
   *  the *live* book would compare a segment with itself the instant it
   *  was written, and nothing would ever be gold. */
  const goldsRef = useRef<Readonly<Record<string, number>>>({});
  const pbRef = useRef<PersonalBest | null>(null);
  const pbAtRef = useRef<Map<string, number>>(new Map());
  /** Committed exactly once per run, whichever path got here first. */
  const recordedRef = useRef(false);
  const splitKeysRef = useRef<string[]>([]);
  /** Text nodes for the in-progress segment. Written from `onFrame`,
   *  never reconciled — same trick as the IGT clock. */
  const liveTimeRef = useRef<HTMLSpanElement | null>(null);
  const liveDeltaRef = useRef<HTMLSpanElement | null>(null);
  const liveQuantumRef = useRef(-1);
  const speakRef = useRef<(l: { text: string; priority: string } | null) => void>(() => {});
  const vctxRef = useRef<(s: SimState) => VoiceContext>((s) => ({
    tick: s.tick,
    status: s.status,
    hpFrac: s.player.hp / s.player.hpMax,
  }));
  /** How far through the opening script we've got. The script is a
   *  queue, not a timetable — see onFrame. */
  const openingIndexRef = useRef(0);
  const lastHudRef = useRef<Hud | null>(null);
  const lockDeniedRef = useRef(false);
  /** Last known canvas box. Refreshed on resize and on pointer entry —
   *  never per frame, because reading it forces a synchronous layout and
   *  we write to the wrapper's style every frame. */
  const rectRef = useRef({ left: 0, top: 0, width: 0, height: 0 });

  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lockDenied, setLockDenied] = useState(false);
  const [results, setResults] = useState<Results | null>(null);
  /** Wall-clock instant after which the results screen accepts a
   *  restart key. Input in flight is not intent. */
  const resultsSettleRef = useRef(0);
  /** The card just cleared, for the transient banner that explains why
   *  the board emptied. Null when nothing recently ended. */
  const [cleared, setCleared] = useState<{ name: string; at: number } | null>(null);
  /** Encoded replay for the finished run, or null while it encodes.
   *  Kept out of `Results` on purpose: encoding takes a moment and the
   *  results screen must not wait on it. */
  const [replay, setReplay] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<
    { phase: "idle" | "sending" } | { phase: "done"; result: SubmitResult }
  >({ phase: "idle" });
  const [drafting, setDrafting] = useState(false);
  const [hud, setHud] = useState<Hud>(() => hudFrom(createRun(config)));

  /** Completed segments. Written a handful of times per run — once per
   *  card — and never per frame. */
  const [rows, setRows] = useState<SplitRow[]>([]);
  const [pbTicks, setPbTicks] = useState<number | null>(null);
  const [sob, setSob] = useState<number | null>(null);
  /**
   * The panel, off until asked for.
   *
   * A split timer is a tool for someone on their fortieth attempt. Shown
   * to a first-time visitor it is a screen full of numbers about a run
   * they have not had yet — so the default is off, the preference is
   * remembered per host, and `S` is the whole interface.
   */
  // Furniture, like the splits panel: it answers on the intro, mid-run,
  // on a draft and on the results screen alike, because "what does this
  // card do" is a question you have at all four of those moments.
  const [codexOpen, setCodexOpen] = useState(false);
  /** The board. Furniture like the codex — it answers on the intro, on
   *  a draft and on the results screen, which is where anyone actually
   *  wants to see whose time they just failed to beat. */
  const [boardOpen, setBoardOpen] = useState(false);
  const [showSplits, setShowSplits] = useState<boolean>(() => {
    try {
      return loadSplitPref(safeStorage(), host.storageKey(SPLITS_PREF_SUFFIX));
    } catch {
      return false;
    }
  });
  const showSplitsRef = useRef(showSplits);
  showSplitsRef.current = showSplits;

  // Live mirrors of state that event handlers read but must not be
  // rebuilt for. Written during render; read only from callbacks.
  const pausedRef = useRef(false);
  pausedRef.current = paused;
  const lockedSignalsRef = useRef(false);
  lockedSignalsRef.current = hud.locked;
  /** Which signals the current phase refuses. Per signal since
   *  single-user stopped being all-or-nothing. */
  const refusedRef = useRef<SignalId[]>([]);
  refusedRef.current = hud.refused;
  const hudRef = useRef(hud);
  hudRef.current = hud;

  /**
   * Why a signal did nothing.
   *
   * The bar showed cost and readiness, and pressing a key you could not
   * afford did *exactly nothing* — no flash, no sound, no line. Which is
   * indistinguishable from a broken keybind, and was reported as one:
   * you start a run on 0 PRIV and every numbered signal costs at least
   * 1, so for the opening stretch of every first run all four keys are
   * dead by design and silent about it.
   *
   * Advisory only. The press is still queued for the sim exactly as
   * before and the sim remains the authority on whether it lands — this
   * must not gate the input, because what reaches `step()` is what gets
   * recorded, and changing that would change every replay.
   */
  const [refusal, setRefusal] = useState<{ id: SignalId; why: string; at: number } | null>(null);
  useEffect(() => {
    if (!cleared) return;
    const t = window.setTimeout(() => setCleared(null), CARD_CLEAR_BANNER_MS);
    return () => window.clearTimeout(t);
  }, [cleared]);

  useEffect(() => {
    if (!refusal) return;
    const t = window.setTimeout(() => setRefusal(null), 1400);
    return () => window.clearTimeout(t);
  }, [refusal]);
  const noteRefusal = useCallback((id: SignalId): void => {
    const h = hudRef.current;
    const i = SIGNALS.findIndex((sig) => sig.id === id);
    if (i < 0) return;
    const def = SIGNALS[i]!;
    const why = h.refused.includes(id)
      ? "single-user — only kill -9 survives"
      : h.priv < def.cost
        ? `needs ${def.cost} PRIV — graze to earn it`
        : (h.cooldowns[i] ?? 0) > 0
          ? "cooling down"
          : "";
    if (!why) return;
    setRefusal({ id, why, at: Date.now() });
  }, []);
  const draftOffersRef = useRef<string[]>([]);
  draftOffersRef.current = hud.offers;

  // Imperative handles, assembled by the engine effect below. React
  // event handlers reach for these instead of closing over engine
  // internals, which keeps the effect's dependency list to one string.
  const apiRef = useRef<{
    begin: () => void;
    restart: () => void;
    resolveDraft: (pick?: string) => void;
    togglePause: () => void;
    resume: () => void;
  } | null>(null);

  const wantLock = POINTER_LOCK_SUPPORTED && !lockDenied;
  const wantLockRef = useRef(wantLock);
  wantLockRef.current = wantLock;

  /**
   * The single source of truth for "is the fight advancing?".
   *
   * Everything that can stop the run — the intro, a draft, a results
   * screen, a pointer that got away — is reconciled here, so the overlay
   * on screen and the loop's pause flag are computed from the same
   * expression and cannot drift apart. The bug this exists to make
   * impossible: an overlay saying "click to resume" over a simulation
   * that is quietly still killing you.
   *
   * (The loop's own tab-hidden / window-blur pause is separate and
   * additive; `pause(false)` here cannot override it.)
   */
  const shouldRun = started && !results && !drafting && !(wantLock && !locked);
  const shouldRunRef = useRef(shouldRun);
  shouldRunRef.current = shouldRun;
  /** Bumped whenever the loop object itself is replaced, so the effect
   *  below reconciles the *new* loop rather than assuming the old one's
   *  pause state carried over. It does not. */
  const [loopGen, setLoopGen] = useState(0);
  useEffect(() => {
    loopRef.current?.pause(!shouldRun);
  }, [shouldRun, loopGen]);

  // ── Renderer ────────────────────────────────────────────────────────
  // Built once and kept for the life of the component. Restarting a run
  // must not touch it — rebuilding a canvas context is the one thing in
  // here slow enough to break the 300ms restart budget.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let renderer: ReturnType<typeof createRenderer> | null = null;
    try {
      renderer = createRenderer(canvas);
    } catch {
      // No 2D context (jsdom, a blocked canvas). The sim, the HUD and
      // the input all still work; only the picture is missing.
      renderer = null;
    }
    rendererRef.current = renderer;

    const measure = (): void => {
      const rect = canvas.getBoundingClientRect();
      rectRef.current = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
      measureView(viewRef.current, rect.width, rect.height, stateRef.current?.arenaScale ?? 1);
      if (rect.width > 0 && rect.height > 0) {
        renderer?.resize(rect.width, rect.height);
        const s = stateRef.current;
        if (s) renderer?.draw(s, 0, now());
      }
    };

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver === "function") {
      ro = new ResizeObserver(measure);
      ro.observe(canvas);
    }
    measure();

    return () => {
      ro?.disconnect();
      renderer?.destroy();
      rendererRef.current = null;
    };
  }, []);

  // ── The engine ──────────────────────────────────────────────────────
  useEffect(() => {
    /** The recorded stream IS the input stream. `record` canonicalises
     *  (quantises) what it stores, and we feed that canonical value to
     *  `step()` rather than the raw reading — otherwise the live run
     *  and its own replay diverge from the very first tick. */
    const readInput = (): SimInput => {
      const tick = tickCountRef.current++;
      let fire = fireArmedRef.current;
      fireArmedRef.current = false;
      if (!fire && holdingRef.current && tick - lastFireTickRef.current >= AUTOFIRE_TICKS) {
        fire = true;
      }
      if (fire) lastFireTickRef.current = tick;

      const signals = pendingSignalsRef.current;
      if (signals.length > 0) pendingSignalsRef.current = [];

      const raw: SimInput = {
        x: cursorRef.current.x,
        y: cursorRef.current.y,
        fire,
        signals,
      };
      // Feed `step()` the value that was stored, not the raw reading.
      return recorderRef.current?.record(raw) ?? raw;
    };

    /**
     * Begin the logbook side of a run.
     *
     * Read once, at the start, and then left alone. Everything the panel
     * compares against — the PB's splits, the golds, the sum of best —
     * is a snapshot taken here, because a comparison that updates itself
     * mid-run compares a segment with the segment that just replaced it.
     */
    const startRecords = (s: SimState): void => {
      splitsRef.current = createSplitTracker(s);
      recordedRef.current = false;
      liveQuantumRef.current = -1;
      setRows([]);

      let records = recordsRef.current;
      try {
        records = readRecords(safeStorage(), hostRef.current.storageKey(RECORDS_SUFFIX));
      } catch {
        records = emptyRecords();
      }
      recordsRef.current = records;
      goldsRef.current = records.golds;

      const keys = planSplitKeys(s);
      splitKeysRef.current = keys;
      const pb = records.pbs[pbKey(LIVE_CATEGORY, s.config.nice)] ?? null;
      pbRef.current = pb;
      pbAtRef.current = referenceAt(pb?.splits);
      setPbTicks(pb ? pb.ticks : null);
      setSob(sumOfBest(records, keys));
    };

    /**
     * Commit the run, once, whatever ended it.
     *
     * Idempotent for the same reason `Telemetry.finish` is: a lost run
     * ends on an event *and* on unmount, and both firing must not count
     * the run twice. A run that never ticked is a page view, not a run —
     * committing it would inflate every lifetime denominator.
     */
    const recordRun = (outcome: "won" | "lost" | "quit", s: SimState): RunRecord | null => {
      if (recordedRef.current) return null;
      const tracker = splitsRef.current;
      if (!tracker || s.tick <= 0) return null;
      recordedRef.current = true;
      let storage: Storage | null = null;
      let key = "";
      try {
        storage = safeStorage();
        key = hostRef.current.storageKey(RECORDS_SUFFIX);
      } catch {
        storage = null;
      }
      const out = commitRun({ state: s, outcome, tracker, storage, key });
      if (out) recordsRef.current = out.records;
      return out;
    };

    const finish = (won: boolean, ticks: number, s: SimState): void => {
      telemetryRef.current?.finish(won ? "won" : "lost", s);
      const record = recordRun(won ? "won" : "lost", s);
      // Pause rather than stop: the killing frame still needs to draw,
      // and the shake still needs to decay. The loop is replaced
      // wholesale on restart anyway.
      loopRef.current?.pause(true);
      exitLock();
      const marks = splitsRef.current?.marks() ?? [];
      resultsSettleRef.current = Date.now() + RESULTS_SETTLE_MS;
      setResults({
        won,
        ticks,
        grazes: s.stats.grazes,
        score: s.stats.score,
        peakLoadMilli: s.stats.peakLoadMilli,
        hitsTaken: s.stats.hitsTaken,
        cardsCleared: s.stats.cardsCleared,
        damageDealt: s.stats.damageDealt,
        signalsFired: s.stats.signalsFired,
        record,
        // Against the book as it stood *before* this run, so a PB shows
        // the deltas that earned it rather than a column of zeroes.
        rows: buildRows(marks, pbRef.current?.splits, goldsRef.current),
        pbTicks: pbRef.current ? pbRef.current.ticks : null,
        sob: sumOfBest(record?.records ?? recordsRef.current, splitKeysRef.current),
      });
      hostRef.current.say(
        won ? "SEGMENTATION FAULT. CORE DUMPED. WELL PLAYED." : "PROCESS TERMINATED. AS PREDICTED.",
        "boss",
      );
      if (won) hostRef.current.unlock("daemon-killed");

      // A win is not the only postable run any more. The load-average
      // board takes losses too — dying on the last card with a huge
      // score is a completely legitimate arcade result, and refusing it
      // would quietly turn the score board back into a speedrun board
      // where the only way to post is to survive.
      setReplay(null);
      setSubmitState({ phase: "idle" });
      if ((won || s.stats.score > 0) && recorderRef.current) {
        const rec = recorderRef.current.finish(s);
        void encodeReplay(rec)
          .then(setReplay)
          // A failed encode costs the board entry, never the run: the
          // time is already in the local logbook either way.
          .catch(() => setReplay(null));
      }
    };

    // `state` is the state *after* the tick that produced these events —
    // which is the only state whose stats include the hit that just
    // landed. Never read `stateRef` here; it is a tick behind.
    // Its own generator, deliberately. If the voice drew from the sim's
    // stream, saying a line would advance the simulation's randomness
    // and every replay would desync — silently, and only sometimes.
    const voice: Voice = createVoice({
      script: DAEMON_LINES,
      rng: rngFromSeed(`${config.seed}:voice`),
    });
    voiceRef.current = voice;
    recorderRef.current = createRecorder(config);
    telemetryRef.current = createTelemetry({ nice: activeConfig.nice, daily: config.daily });

    const vctx = (state: SimState): VoiceContext => ({
      tick: state.tick,
      status: state.status,
      hpFrac: state.player.hp / state.player.hpMax,
    });

    const speak = (line: { text: string; priority: string } | null): void => {
      if (!line) return;
      hostRef.current.say(line.text, line.priority === "critical" ? "boss" : "normal");
    };
    speakRef.current = speak;
    vctxRef.current = vctx;

    const onEvents = (events: SimEvent[], state: SimState): void => {
      // Splits first, and that ordering is load-bearing: `won` and
      // `lost` arrive in the same batch as the `card-clear` that caused
      // them, and `finish` reads the tracker. Observe after the switch
      // and the final segment of every winning run would be missing.
      const tracker = splitsRef.current;
      if (tracker) {
        const before = tracker.marks().length;
        tracker.observe(events, state);
        if (tracker.marks().length !== before) {
          // Once per cleared card. Not per tick, and nowhere near 60Hz.
          setRows(buildRows(tracker.marks(), pbRef.current?.splits, goldsRef.current));
          liveQuantumRef.current = -1;
        }
      }

      const reduced = hostRef.current.reducedMotion();
      for (const e of events) {
        switch (e.t) {
          case "player-hit":
            if (!reduced) shakeRef.current = Math.max(shakeRef.current, SHAKE_HIT);
            flashRef.current = 1;
            break;
          case "daemon-hit":
            if (e.crit && !reduced) shakeRef.current = Math.max(shakeRef.current, SHAKE_CRIT);
            break;
          case "card-start":
            // The banner is the HUD's job; the line is the voice's.
            break;
          case "card-clear":
            // Clearing a card wipes the board — `s.bullets = []` in the
            // sim — and that is deliberate: the next card starts from a
            // clean field rather than inheriting a screen of someone
            // else's pattern. But nothing on screen said so, so a card
            // timing out looked exactly like the playfield glitching
            // and eating every bullet at random.
            //
            // Announcing it costs one transient banner and turns "what
            // just happened" into "I cleared that". Presentation only —
            // the sim already did the wipe and does not know this
            // exists.
            setCleared({ name: e.name, at: Date.now() });
            break;
          case "phase-clear":
            // The last phase clears straight into a win, with no draft
            // between; only offer one if the sim actually stopped for it.
            if (state.status === "draft") {
              setDrafting(true);
              loopRef.current?.pause(true);
              // Give the cursor back. The draft is a screen you click,
              // and pointer lock means there is no cursor to click it
              // with — the cards were unreachable by mouse entirely,
              // reported as "the game has captured my mouse input".
              // Keyboard 1/2/3 still worked, which is exactly why this
              // survived: every automated check drives the keyboard.
              if (typeof document !== "undefined" && document.pointerLockElement) exitLock();
            }
            break;
          case "shrink":
            // The host owns the frame; `onFrame` reports the scale.
            break;
          case "won":
            finish(true, e.ticks, state);
            break;
          case "lost":
            finish(false, e.ticks, state);
            break;
          default:
            break;
        }
      }

      // One call, whatever happened this tick. Priority ordering,
      // deduplication, rate limiting and suppression all live in the
      // voice — a flood of thirty grazes yields exactly one line, and a
      // player-down still cuts in front of it.
      speak(voice.observe(events, vctx(state)));
      telemetryRef.current?.observe(events, state);
    };

    const onFrame = (s: SimState, alpha: number): void => {
      stateRef.current = s;

      // The tutorial. Not a screen, not a tooltip — four lines that
      // teach dodging, then grazing, then signals, in the order each
      // becomes useful.
      //
      // Treated as a QUEUE rather than a timetable, and that distinction
      // is load-bearing. A line is due at a tick, but it only speaks
      // once DAEMON is actually quiet: an utterance can run 400+ ticks,
      // and a `beat` cannot interrupt another `beat`, so fixed cue times
      // meant the second and third lessons were silently swallowed by
      // the first. Waiting for air costs a beat of delay and delivers
      // all four; firing on the clock delivered two, and the two it
      // dropped were grazing and signals — the mechanics nobody can
      // guess.
      const voice = voiceRef.current;
      const nextCue = OPENING_SCRIPT[openingIndexRef.current];
      if (voice && nextCue && s.tick >= nextCue.tick && !voice.speaking(s.tick)) {
        openingIndexRef.current += 1;
        speakRef.current(voice.cue(nextCue.pool, vctxRef.current(s)));
      }

      if (s.arenaScale !== lastScaleRef.current) {
        lastScaleRef.current = s.arenaScale;
        // The host animates a real window frame (skenOS) or the drawn
        // walls (standalone); either way our own inverse transform has
        // to follow, or the cursor stops agreeing with the hitbox.
        hostRef.current.setArenaScale(s.arenaScale);
        const r = rectRef.current;
        measureView(viewRef.current, r.width, r.height, s.arenaScale);
      }

      rendererRef.current?.draw(s, alpha, now());

      // Shake and flash live on the wrapper as custom properties so the
      // CSS decides what to do with them — and so reduced-motion users
      // simply never see a non-zero value.
      const wrap = wrapRef.current;
      if (wrap) {
        if (shakeRef.current > 0.05) {
          shakeRef.current *= SHAKE_DECAY;
          const a = shakeRef.current;
          wrap.style.setProperty("--dg-shake-x", `${(Math.random() * 2 - 1) * a}px`);
          wrap.style.setProperty("--dg-shake-y", `${(Math.random() * 2 - 1) * a}px`);
        } else if (shakeRef.current !== 0) {
          shakeRef.current = 0;
          wrap.style.setProperty("--dg-shake-x", "0px");
          wrap.style.setProperty("--dg-shake-y", "0px");
        }
        if (flashRef.current > 0.01) {
          flashRef.current *= 0.82;
          wrap.style.setProperty("--dg-flash", `${flashRef.current.toFixed(3)}`);
        } else if (flashRef.current !== 0) {
          flashRef.current = 0;
          wrap.style.setProperty("--dg-flash", "0");
        }
      }

      // The run timer is IGT — simulation ticks, not wall-clock — so it
      // is immune to frame drops and to a throttled background tab.
      // Written straight to the text node; see the note on `Hud`.
      if (timerRef.current) timerRef.current.textContent = formatTicks(s.tick);

      // The in-progress segment. Two text writes, ten times a second,
      // and no reconciliation at all — the panel's React state only
      // moves when a card is actually cleared.
      if (showSplitsRef.current) {
        const q = Math.floor(s.tick / LIVE_QUANTUM);
        if (q !== liveQuantumRef.current) {
          liveQuantumRef.current = q;
          const marks = splitsRef.current?.marks();
          const since = marks && marks.length > 0 ? (marks[marks.length - 1]?.at ?? 0) : 0;
          if (liveTimeRef.current) liveTimeRef.current.textContent = formatTicks(s.tick - since);
          const el = liveDeltaRef.current;
          if (el) {
            // LiveSplit's rule: the live delta only appears once you are
            // actually late. Showing "ahead" before the segment ends
            // would be a promise the run has not made yet.
            const key = splitsRef.current?.lastCard();
            const ref = key === null || key === undefined ? undefined : pbAtRef.current.get(key);
            const behind = ref !== undefined && s.tick > ref;
            el.textContent = behind ? formatDelta(s.tick - (ref as number)) : "";
            el.dataset.ahead = behind ? "no" : "none";
          }
        }
      }

      const model = hudFrom(s);
      if (!lastHudRef.current || !hudEq(model, lastHudRef.current)) {
        lastHudRef.current = model;
        setHud(model);
      }
    };

    const makeLoop = (state: SimState, prevInput?: SimInput): Loop =>
      createLoop({
        state,
        prevInput,
        readInput,
        onFrame,
        onEvents,
        onPause: (p) => {
          setPaused(p);
          // Never leave the fire button "stuck down" across a pause.
          if (p) holdingRef.current = false;
        },
      });

    const resetInput = (): void => {
      cursorRef.current = { x: EMPTY_INPUT.x, y: EMPTY_INPUT.y };
      fireArmedRef.current = false;
      holdingRef.current = false;
      pendingSignalsRef.current = [];
      tickCountRef.current = 0;
      lastFireTickRef.current = -AUTOFIRE_TICKS;
      shakeRef.current = 0;
      flashRef.current = 0;
    };

    const swapRun = (state: SimState, prevInput?: SimInput): void => {
      loopRef.current?.stop();
      stateRef.current = state;
      lastHudRef.current = null;
      const loop = makeLoop(state, prevInput);
      loopRef.current = loop;
      loop.start();
      // A fresh loop starts running, which is a lie whenever the gate
      // says otherwise — and the gate's effect could not correct it,
      // because after a draft resolved with the pointer loose the value
      // went false → false and an effect that does not change does not
      // re-run. The result was the exact failure the gate exists to
      // prevent: a SIGSTOP overlay on screen over a simulation still
      // killing you behind it.
      //
      // Two halves. This one shuts the new loop immediately, from a ref,
      // so nothing ticks in the gap before React re-renders...
      loop.pause(!shouldRunRef.current);
      // ...and this makes the reconciling effect re-run on every swap
      // regardless of whether `shouldRun` itself moved, which is what
      // makes the correction reliable rather than incidental.
      setLoopGen((g) => g + 1);
    };

    // Fresh run, not started. `status` stays "intro" so the renderer can
    // draw its title card; the sim is not stepped until the first click.
    const fresh = createRun(configRef.current);
    stateRef.current = fresh;
    lastHudRef.current = null;
    startRecords(fresh);
    setHud(hudFrom(fresh));
    loopRef.current = makeLoop(fresh);
    setStarted(false);
    setPaused(false);
    setResults(null);
    setDrafting(false);
    resetInput();
    rendererRef.current?.draw(fresh, 0, now());

    apiRef.current = {
      begin: () => {
        const s = stateRef.current;
        if (!s) return;
        // Safe: nothing has stepped yet, so the loop's `state` and this
        // object are the same reference and `step` has not copied it.
        if (s.status === "intro") s.status = "running";
        setStarted(true);
        loopRef.current?.pause(false);
        loopRef.current?.start();
      },
      resume: () => {
        loopRef.current?.pause(false);
      },
      togglePause: () => {
        if (wantLockRef.current) {
          // With pointer lock in play, "paused" and "the pointer is
          // loose" are the same state — there is only one thing to
          // toggle. Releasing pauses; getting it back needs a click,
          // because only a click can ask a browser for the cursor.
          if (typeof document !== "undefined" && document.pointerLockElement) exitLock();
          return;
        }
        loopRef.current?.pause(!pausedRef.current);
      },
      restart: () => {
        // Shift-R in the middle of a run that was going badly is still a
        // run that happened. Committing it as a quit is what keeps the
        // win rate a number rather than a compliment — and it is a no-op
        // when the run already ended, because `recordRun` is idempotent.
        const previous = stateRef.current;
        if (previous) recordRun("quit", previous);

        const s = createRun(configRef.current);
        s.status = "running";
        trackRestart(activeConfig.nice, results?.won ? "won" : "lost");
        startRecords(s);
        resetInput();
        setResults(null);
        setDrafting(false);
        setStarted(true);
        swapRun(s);
      },
      resolveDraft: (pick?: string) => {
        const s = stateRef.current;
        if (!s || s.status !== "draft") return;
        // The sim owns the transition — including the case where the
        // draft was the last one and resolving it wins the run. All we
        // do is choose, hand it over, and drain what comes back.
        // Recorded as an INDEX into the offers, not an upgrade id: the
        // offers are themselves derived from the seed, so the index is
        // both smaller and self-verifying — a replay that produces
        // different offers will pick differently and fail verification
        // rather than silently diverging.
        trackDraft(s.offers, pick ?? null, activeConfig.nice);
        const pickIndex = pick ? s.offers.indexOf(pick) : -1;
        recorderRef.current?.draft(s.tick, Math.max(0, pickIndex));
        const next = resolveDraft(s, pick);
        setDrafting(false);
        // Carry the live cursor into the new loop: the first tick of a
        // phase must not see a velocity spike from the arena's centre,
        // or the opening `lead` shot aims at a movement you didn't make.
        swapRun(next, { ...EMPTY_INPUT, x: cursorRef.current.x, y: cursorRef.current.y });
        if (next.events.length > 0) onEvents(next.events, next);
      },
    };

    // Abandonment is the signal nobody reports and the one that matters
    // most — a run that ends because someone closed the tab is the shape
    // of losing interest, and it never shows up as a loss. `pagehide`
    // fires on close, navigate and bfcache; unmount covers closing the
    // window inside skenOS. `finish` is idempotent, so both firing is
    // fine and neither firing would lose the run.
    const onLeave = (): void => {
      const st = stateRef.current;
      if (!st) return;
      telemetryRef.current?.finish("quit", st);
      // Same reasoning, same idempotence. A run abandoned at the tab
      // close is the one nobody would ever report, and it is exactly the
      // one the lifetime counters need in order to be true.
      recordRun("quit", st);
    };
    window.addEventListener("pagehide", onLeave);

    return () => {
      window.removeEventListener("pagehide", onLeave);
      onLeave();
      loopRef.current?.stop();
      loopRef.current = null;
      apiRef.current = null;
      // Hand the window frame back the way we found it. Leaving a
      // skenOS window shrunk after the app closes is exactly the kind of
      // leak this rebuild exists to delete.
      hostRef.current.setArenaScale(1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  // ── Pointer lock ────────────────────────────────────────────────────
  useEffect(() => {
    if (!POINTER_LOCK_SUPPORTED) return;
    const onChange = (): void => {
      const isLocked = document.pointerLockElement === canvasRef.current;
      setLocked(isLocked);
      if (!isLocked) {
        // Escape, alt-tab, or the browser reclaiming the cursor. Freeze
        // immediately — a bullet hell you cannot aim is a death sentence
        // — and let the overlay explain itself.
        holdingRef.current = false;
        loopRef.current?.pause(true);
      }
    };
    const onError = (): void => {
      // Lock refused (an iframe without allow-pointer-lock, a transient
      // Chrome throttle after the user pressed Escape). Degrade to plain
      // cursor tracking rather than stranding the player.
      lockDeniedRef.current = true;
      setLockDenied(true);
      setLocked(false);
      loopRef.current?.pause(false);
    };
    document.addEventListener("pointerlockchange", onChange);
    document.addEventListener("pointerlockerror", onError);
    return () => {
      document.removeEventListener("pointerlockchange", onChange);
      document.removeEventListener("pointerlockerror", onError);
      if (document.pointerLockElement === canvasRef.current) exitLock();
    };
  }, []);

  const requestLock = useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.focus();
    if (!POINTER_LOCK_SUPPORTED || lockDeniedRef.current) return;
    if (document.pointerLockElement === canvas) return;
    try {
      const ret = (
        canvas as HTMLCanvasElement & { requestPointerLock: () => unknown }
      ).requestPointerLock();
      if (ret && typeof (ret as Promise<void>).catch === "function") {
        (ret as Promise<void>).catch(() => {
          lockDeniedRef.current = true;
          setLockDenied(true);
          loopRef.current?.pause(false);
        });
      }
    } catch {
      lockDeniedRef.current = true;
      setLockDenied(true);
    }
  }, []);

  // ── Input handlers ──────────────────────────────────────────────────

  /** Re-read the canvas box. Cheap enough at pointer-entry rate, far too
   *  expensive at pointer-move rate. */
  const remeasure = useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    rectRef.current = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    measureView(viewRef.current, rect.width, rect.height, stateRef.current?.arenaScale ?? 1);
  }, []);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const c = cursorRef.current;

    const v = viewRef.current;
    // A zero-width canvas (jsdom, or a window mid-animation) would make
    // every coordinate Infinity. Refuse to move rather than teleport.
    if (!(v.scale > 0)) return;

    if (typeof document !== "undefined" && document.pointerLockElement === canvas) {
      // Locked: there is no cursor to read, only deltas. Divide by the
      // letterbox scale so a given hand movement covers the same arena
      // distance regardless of how big the window is — and so the
      // shrink device makes the fight tighter, not the mouse heavier.
      c.x = clamp(c.x + (e.movementX * SENSITIVITY) / v.scale, 0, ARENA_W);
      c.y = clamp(c.y + (e.movementY * SENSITIVITY) / v.scale, 0, ARENA_H);
      return;
    }

    const rect = rectRef.current;
    c.x = clamp((e.clientX - rect.left - v.ox) / v.scale, 0, ARENA_W);
    c.y = clamp((e.clientY - rect.top - v.oy) / v.scale, 0, ARENA_H);
  }, []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): void => {
      const api = apiRef.current;
      if (!api) return;

      // Let real controls handle themselves. pointerdown precedes click,
      // so without this a tap on a draft card would resolve the draft
      // with no pick before the card's own onClick ever ran.
      if ((e.target as HTMLElement | null)?.closest("button, a, [role=button]")) return;

      // `button` defaults to 0 (primary) per spec, and synthetic events
      // can omit it entirely — jsdom has no PointerEvent constructor at
      // all. Treating a missing value as anything but primary makes the
      // handler untestable and would drop real clicks in any
      // environment that dispatches a bare event.
      const button = e.button ?? 0;

      if (button === 2) {
        // Right-click is the panic button. SIGSTOP is the one signal you
        // reach for without looking, so it gets the mouse.
        // SIGSTOP is not the exception; SIGKILL is. Single-user still
        // refuses this one, and the HUD says so.
        if (!refusedRef.current.includes("sigstop")) pendingSignalsRef.current.push("sigstop");
        return;
      }
      if (button !== 0) return;

      if (results) {
        // Deliberately does nothing.
        //
        // This used to restart on any click. You die with the mouse
        // button already moving — you have been mashing it to fire
        // SIGTERM for four minutes — and that click lands on a results
        // screen that appeared a frame ago, restarting instantly and
        // taking the submit form with it. The run is gone before it can
        // be posted, and the claim code with it.
        //
        // Restarting is now a button, or a key. Both are deliberate in a
        // way that "the mouse was already down" is not.
        return;
      }
      if (drafting) {
        api.resolveDraft();
        requestLock();
        return;
      }
      if (!started) {
        api.begin();
        requestLock();
        return;
      }
      if (wantLock && !locked) {
        // This click buys back the pointer; it does not also fire.
        // Nothing is more annoying than a resume that costs a cooldown.
        // The run restarts on its own once the lock actually lands —
        // see `shouldRun`.
        requestLock();
        return;
      }
      if (paused) {
        api.resume();
        return;
      }

      fireArmedRef.current = true;
      holdingRef.current = true;
    },
    [results, drafting, started, locked, paused, wantLock, requestLock],
  );

  const releaseFire = useCallback((): void => {
    holdingRef.current = false;
  }, []);

  /**
   * Show or hide the split panel, and remember the answer.
   *
   * `S` is free: the signal row is digits, pause is `P`, restart is `R`,
   * and skenOS's own shortcuts are all modified. The preference is
   * stored per host, so turning it on standalone does not turn it on
   * inside the OS — they are different logbooks and, in practice,
   * different moods.
   */
  const toggleSplits = useCallback((): void => {
    setShowSplits((on) => {
      const next = !on;
      try {
        saveSplitPref(next, safeStorage(), hostRef.current.storageKey(SPLITS_PREF_SUFFIX));
      } catch {
        /* the panel still works, it just forgets */
      }
      return next;
    });
    // The live row's text nodes are written from `onFrame` only when the
    // quantum moves; force the next frame to repaint them so the panel
    // is not blank for a tenth of a second on open.
    liveQuantumRef.current = -1;
  }, []);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>): void => {
      const api = apiRef.current;
      if (!api) return;
      // Never steal a keystroke that is being typed into a field.
      //
      // Every hotkey here is a bare letter, and the results screen has a
      // text input on it for your initials. So typing a name containing
      // S opened the splits panel and swallowed the character; C opened
      // the codex; L opened the board; and R, Enter or Space restarted
      // the run outright — destroying the very run the form existed to
      // submit, along with its claim code.
      //
      // Checked before anything else, including the modifier guard,
      // because a field has a stronger claim on a keystroke than any
      // shortcut does. `closest` rather than a tag check so it holds for
      // anything focusable we put in a form later.
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;

      // Never swallow a modified keystroke: Cmd-W, Ctrl-C and the rest
      // belong to the browser and to skenOS, not to us.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const code = e.code;
      const key = e.key;

      // Before every state branch, because the panel is furniture rather
      // than a game verb: it has to answer on the intro, mid-run, on a
      // draft and on the results screen alike.
      if (code === "KeyS") {
        e.preventDefault();
        toggleSplits();
        return;
      }

      if (code === "KeyC") {
        e.preventDefault();
        setCodexOpen((v) => !v);
        return;
      }

      if (code === "KeyL") {
        e.preventDefault();
        setBoardOpen((v) => !v);
        return;
      }
      if (boardOpen && key === "Escape") {
        e.preventDefault();
        setBoardOpen(false);
        return;
      }
      // Escape closes the codex before anything else gets to act on it,
      // so the key that dismisses every other panel dismisses this one
      // rather than releasing the pointer out from under it.
      if (codexOpen && key === "Escape") {
        e.preventDefault();
        setCodexOpen(false);
        return;
      }

      if (results) {
        if (code === "KeyR" || code === "Enter" || code === "Space" || key === "Enter") {
          e.preventDefault();
          // Same reasoning as the click above, for the keyboard. A key
          // held or repeating as the run ends must not spend the
          // results screen before it has been read.
          if (Date.now() < resultsSettleRef.current) return;
          api.restart();
          requestLock();
        }
        return;
      }
      if (drafting) {
        const slot = DRAFT_KEYS[code] ?? DRAFT_KEYS[`Digit${key}`];
        if (slot !== undefined) {
          const pick = draftOffersRef.current[slot];
          if (pick) {
            e.preventDefault();
            api.resolveDraft(pick);
            requestLock();
          }
          return;
        }
        if (code === "Space" || code === "Enter" || key === "Enter") {
          e.preventDefault();
          api.resolveDraft();
          requestLock();
        }
        return;
      }
      if (!started) {
        if (code === "Space" || code === "Enter" || key === "Enter") {
          e.preventDefault();
          api.begin();
          requestLock();
        }
        return;
      }

      const signal = SIGNAL_KEYS[code] ?? SIGNAL_KEYS[`Digit${key}`];
      if (signal) {
        // Consumed even in runlevel 1, where the signal does nothing:
        // swallowing the key and showing a locked bar is honest, and
        // letting `4` scroll the page instead is not.
        e.preventDefault();
        if (!refusedRef.current.includes(signal)) pendingSignalsRef.current.push(signal);
        noteRefusal(signal);
        return;
      }
      if (code === "KeyP") {
        e.preventDefault();
        api.togglePause();
        return;
      }
      if (code === "KeyR" && e.shiftKey) {
        // Shift-R restarts mid-run. Deliberately shifted: an unmodified
        // `r` in the middle of a good run would be a disaster.
        e.preventDefault();
        api.restart();
        requestLock();
        return;
      }
      // Everything else — Escape included — is left alone. Escape is how
      // the browser gives the cursor back and we must not fight it.
    },
    [results, drafting, started, requestLock, toggleSplits, codexOpen, boardOpen, noteRefusal],
  );

  const onContextMenu = useCallback((e: ReactMouseEvent): void => {
    // Right-click is a game verb here; the menu would cover the arena.
    e.preventDefault();
  }, []);

  const phase = useMemo(() => phaseAt(hud.phaseIndex), [hud.phaseIndex]);

  const overlay = results
    ? "results"
    : drafting
      ? "draft"
      : !started
        ? "intro"
        : paused || (wantLock && !locked)
          ? "paused"
          : null;

  return (
    <div className="dg" ref={wrapRef} onKeyDown={onKeyDown} data-overlay={overlay ?? "none"}>
      {/* --- top bar: DAEMON, the card, the clock --- */}
      <div className="dg-bar dg-bar-top">
        <div className="dg-boss">
          <div className="dg-boss-line">
            <span className="dg-boss-name">DAEMON</span>
            <span className="dg-phase">{phase.name}</span>
            {/* DAEMON's OWN nice for this runlevel, which is a
                different number from the run's difficulty — also called
                nice, also on screen, also numeric. The joke is that
                DAEMON renices itself lower as the system comes down;
                the confusion is that both are "nice". Saying whose it
                is costs three characters. */}
            <span
              className="dg-nice"
              title="DAEMON's own scheduling priority this runlevel. It renices itself lower as the system comes down. Not your difficulty."
            >
              its nice {phase.nice}
            </span>
            {hud.guarded ? <span className="dg-guard">handler installed</span> : null}
          </div>
          <Meter
            className="dg-meter-boss"
            value={hud.daemonHp}
            max={hud.daemonHpMax}
            label={`${hud.daemonHp} / ${hud.daemonHpMax}`}
          />
        </div>
        {/* The attack on screen, and how long it has left before it
            times out. Unlabelled, this read as a greeting with a
            mysterious number under it — the opening card is called
            "Hello, Operator", which is a good joke that lands as
            confusion when nothing says what the field is. */}
        <div
          className="dg-card"
          title="The card — DAEMON's current attack — and the time left before it clears itself."
        >
          <span className="dg-card-label">card</span>
          <span className="dg-card-name">{hud.cardName || "—"}</span>
          <span className="dg-card-timer">{hud.cardSeconds}s left</span>
        </div>
        <div className="dg-clock">
          <span className="dg-clock-label">IGT</span>
          <span className="dg-clock-time" ref={timerRef}>
            {formatTicks(0)}
          </span>
        </div>
      </div>

      {/* --- the arena --- */}
      {/* Pointer handling lives on the STAGE, not the canvas.
        *
        * The overlay is a sibling of the canvas, absolutely positioned
        * over it — so a click on "Click to begin" never reaches a
        * handler bound to the canvas, and the game could not be started
        * at all. The stage is the one element that spans both. */}
      <div
        // Focusable, and this is load-bearing rather than tidy.
        //
        // The key handler lives on `.dg`, so a keystroke only reaches it
        // if something *inside* `.dg` has focus. `requestLock` focuses
        // the canvas, but a pointerdown's own default focus handling
        // runs afterwards and walks up from the hit element to the
        // nearest focusable ancestor — and there wasn't one, so focus
        // landed on `<body>`, which is an ANCESTOR of `.dg` and
        // therefore never sees the event bubble.
        //
        // The effect was total: with pointer lock denied or released,
        // every key in the game went dead — signals, P, S, C, Shift-R,
        // all of it — while looking perfectly alive. Observed directly:
        // keydown arrived at `window` with `document.activeElement`
        // stuck on BODY, and never reached `.dg` at all.
        //
        // Making the stage focusable gives that default walk somewhere
        // to land that is inside the component, which fixes it without
        // fighting the browser over event ordering.
        tabIndex={-1}
        className="dg-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerEnter={remeasure}
        onPointerUp={releaseFire}
        onPointerLeave={releaseFire}
        onPointerCancel={releaseFire}
        onContextMenu={onContextMenu}
      >
        <canvas
          ref={canvasRef}
          className="dg-canvas"
          tabIndex={0}
          aria-label="DAEMON arena"
        />
        {boardOpen ? (
          <div className="dg-codex-wrap">
            <LeaderboardPanel
              initialNice={config.nice}
              seed={config.seed}
              onClose={() => setBoardOpen(false)}
            />
          </div>
        ) : null}
        {codexOpen ? (
          <div className="dg-codex-wrap">
            <UpgradeCodex held={hud.upgrades} onClose={() => setCodexOpen(false)} />
          </div>
        ) : null}
        {showSplits ? (
          <SplitPanel
            rows={rows}
            phase={phase.id}
            cardName={hud.cardName}
            pbTicks={pbTicks}
            sob={sob}
            timeRef={liveTimeRef}
            deltaRef={liveDeltaRef}
          />
        ) : null}
        {overlay ? (
          <div className="dg-overlay" data-kind={overlay}>
            {overlay === "intro" ? (
              <NicePlate
                nice={activeConfig.nice}
                onPick={setNicePick}
                lines={[
                  "Your cursor is the process. The dot is the whole hitbox.",
                  "Left click sends SIGTERM. Skim a bullet to earn PRIV.",
                  "1 SIGSTOP · 2 SIGKILL · 3 renice · 4 strace · right-click SIGSTOP",
                  "Clear a runlevel and you draft an upgrade. C opens the pool, L the board.",
                ]}
              />
            ) : null}
            {overlay === "paused" ? (
              <Plate
                title="SIGSTOP"
                lines={[
                  wantLock && !locked
                    ? "The pointer is loose, so the fight is frozen. Nothing is advancing without you."
                    : "Paused. Nothing is advancing.",
                  wantLock
                    ? "Escape releases the pointer at any time."
                    : "P resumes. Shift-R starts over.",
                ]}
                cta={wantLock ? "Click to resume" : "Press P to resume"}
              />
            ) : null}
            {overlay === "draft" ? (
              <DraftPlate
                phaseName={phase.name}
                offers={hud.offers}
                onPick={(id) => {
                  apiRef.current?.resolveDraft(id);
                  requestLock();
                }}
                onSkip={() => {
                  apiRef.current?.resolveDraft();
                  requestLock();
                }}
              />
            ) : null}
            {overlay === "results" && results ? (
              <ResultsPlate
                results={results}
                replay={replay}
                submitState={submitState}
                onSubmit={(initials) => {
                  if (!replay) return;
                  setSubmitState({ phase: "sending" });
                  void submitRun(replay, initials).then((result) =>
                    setSubmitState({ phase: "done", result }),
                  );
                }}
                onBoard={() => setBoardOpen(true)}
                onAgain={() => {
                  apiRef.current?.restart();
                  requestLock();
                }}
              />
            ) : null}
          </div>
        ) : null}
        {/* Why the board just emptied. Clearing a card wipes every
            bullet — deliberate, so the next card starts clean — but
            unannounced it reads as the playfield glitching. */}
        {cleared ? (
          <div className="dg-cleared" role="status">
            <b>{cleared.name}</b>
            <span>cleared — board wiped</span>
          </div>
        ) : null}
        <div className="dg-flash" aria-hidden="true" />
      </div>

      {/* --- bottom bar: you --- */}
      <div className="dg-bar dg-bar-bottom">
        <div className="dg-self">
          <span className="dg-self-label">operator</span>
          <Meter
            className="dg-meter-player"
            value={hud.playerHp}
            max={hud.playerHpMax}
            label={`${hud.playerHp} / ${hud.playerHpMax}`}
          />
        </div>
        {/* Score and load, together, because neither reads without the
            other: the number is what you have, the multiplier is what
            you are risking to keep earning it. Load is shown to two
            decimals like `uptime` reports it, which is where the name
            comes from. */}
        <div
          className="dg-load"
          data-hot={hud.loadCenti >= 400 ? "yes" : "no"}
          title="Load average — raised by grazing, bled by playing safe, collapsed by a hit"
        >
          <span className="dg-load-score">{hud.score.toLocaleString()}</span>
          <span className="dg-load-avg">load {(hud.loadCenti / 100).toFixed(2)}</span>
          <span className="dg-load-bar">
            <i style={{ transform: `scaleX(${Math.min(1, hud.loadCenti / 800)})` }} />
          </span>
        </div>
        <div className="dg-priv" title="PRIV — earned by grazing, spent on signals, lost on a hit">
          <span className="dg-priv-label">PRIV</span>
          <span className="dg-priv-pips">
            {Array.from({ length: hud.privMax }, (_, i) => (
              <i key={i} className={i < hud.priv ? "on" : ""} />
            ))}
          </span>
        </div>
        <div className="dg-signals" data-locked={hud.locked ? "yes" : "no"}>
          {hud.locked ? (
            <span className="dg-locked">single-user — only kill -9 survives</span>
          ) : null}
          {refusal && !hud.locked ? (
            <span className="dg-refusal" role="status">
              {refusal.why}
            </span>
          ) : null}
          {SIGNALS.map((sig, i) => {
            const cd = hud.cooldowns[i] ?? 0;
            const poor = hud.priv < sig.cost;
            // Single-user refuses everything but SIGKILL, so "locked" is
            // per signal now rather than a blanket over the whole row.
            const refused = hud.refused.includes(sig.id);
            return (
              <button
                key={sig.id}
                type="button"
                className="dg-signal"
                data-ready={!refused && cd <= 0 && !poor ? "yes" : "no"}
                data-refused={refusal?.id === sig.id ? "yes" : "no"}
                disabled={refused}
                title={
                  refused
                    ? "Runlevel 1 has taken this away. SIGKILL cannot be caught, blocked or ignored — that one still works."
                    : sig.blurb
                }
                tabIndex={-1}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (!refused) pendingSignalsRef.current.push(sig.id);
                  noteRefusal(sig.id);
                }}
              >
                <span className="dg-signal-key">{SIGNAL_KEY_HINT[sig.id]}</span>
                <span className="dg-signal-name">{sig.name}</span>
                <span className="dg-signal-cost">{sig.cost > 0 ? `${sig.cost}` : "free"}</span>
                <span className="dg-signal-cd" style={{ transform: `scaleX(${cd})` }} />
              </button>
            );
          })}
        </div>
        <BuildStrip held={hud.upgrades} />
      </div>
    </div>
  );
}

// ── Bits ──────────────────────────────────────────────────────────────

/**
 * The live split panel.
 *
 * LiveSplit's conventions, because they are not arbitrary — a runner
 * reads this in peripheral vision while dodging, and every one of them
 * already knows what the colours mean. Signed cumulative delta, green
 * ahead, red behind, a gold marker on a segment nobody has ever beaten.
 *
 * It renders when a card is cleared and at no other time. The only thing
 * that moves at frame rate is the bottom row's two text nodes, written
 * imperatively from `onFrame` — which is why they arrive as refs rather
 * than as props.
 *
 * `pointer-events: none` in the stylesheet, and that matters: this sits
 * over the arena, and a panel that ate a SIGTERM would be a bug the
 * player would blame on the game.
 */
function SplitPanel({
  rows,
  phase,
  cardName,
  pbTicks,
  sob,
  timeRef,
  deltaRef,
}: {
  rows: SplitRow[];
  phase: string;
  cardName: string;
  pbTicks: number | null;
  sob: number | null;
  timeRef: RefObject<HTMLSpanElement>;
  deltaRef: RefObject<HTMLSpanElement>;
}): JSX.Element {
  return (
    <aside className="dg-splits" aria-label="splits" aria-live="off">
      <header className="dg-splits-head">
        <span className="dg-splits-title">splits</span>
        <span className="dg-splits-cat">{categoryById(LIVE_CATEGORY)?.name ?? LIVE_CATEGORY}</span>
        <span className="dg-splits-pb">{pbTicks === null ? "no pb" : formatTicks(pbTicks)}</span>
      </header>
      <ol className="dg-splits-list">
        {rows.map((r) => (
          <li
            key={r.key}
            className="dg-split"
            data-gold={r.gold ? "yes" : "no"}
            data-ahead={r.delta === null ? "none" : r.delta < 0 ? "yes" : "no"}
          >
            <span className="dg-split-phase">{r.phase}</span>
            <span className="dg-split-name">{r.name}</span>
            <span className="dg-split-delta">{r.delta === null ? "—" : formatDelta(r.delta)}</span>
            <span className="dg-split-at">{formatTicks(r.at)}</span>
          </li>
        ))}
        <li className="dg-split dg-split-live" data-gold="no">
          <span className="dg-split-phase">{phase}</span>
          <span className="dg-split-name">{cardName || "—"}</span>
          <span className="dg-split-delta" ref={deltaRef} data-ahead="none" />
          <span className="dg-split-at" ref={timeRef}>
            {formatTicks(0)}
          </span>
        </li>
      </ol>
      <footer className="dg-splits-foot">
        <span>sum of best</span>
        <span className="dg-splits-sob">{sob === null ? "—" : formatTicks(sob)}</span>
      </footer>
    </aside>
  );
}

function Meter({
  value,
  max,
  label,
  className,
}: {
  value: number;
  max: number;
  label: string;
  className?: string;
}): JSX.Element {
  const pct = max > 0 ? clamp(value / max, 0, 1) : 0;
  return (
    <div
      className={`dg-meter${className ? ` ${className}` : ""}`}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
    >
      <i style={{ transform: `scaleX(${pct})` }} />
      <span>{label}</span>
    </div>
  );
}

/**
 * The draft, kept deliberately plain.
 *
 * The sim has already rolled the offers from its own seeded generator,
 * so this screen is part of the replay rather than a decision made
 * outside it — all that is left is to show three cards and take one.
 * `1`/`2`/`3` pick, `Space` skips; the buttons exist for the mouse.
 */
function DraftPlate({
  phaseName,
  offers,
  onPick,
  onSkip,
}: {
  phaseName: string;
  offers: string[];
  onPick: (id: string) => void;
  onSkip: () => void;
}): JSX.Element {
  const defs = useMemo(() => describeOffers(offers), [offers]);
  const first = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    first.current?.focus();
  }, []);
  return (
    <div className="dg-plate dg-draft">
      <h2>{phaseName} — cleared</h2>
      <p>DAEMON renices itself further out of your reach. Take something with you.</p>
      <div className="dg-offers">
        {defs.map((u, i) => (
          <button
            key={u.id}
            ref={i === 0 ? first : undefined}
            type="button"
            className="dg-offer"
            data-rarity={u.rarity}
            onClick={() => onPick(u.id)}
          >
            <span className="dg-offer-key">{i + 1}</span>
            <span className="dg-offer-name">{u.name}</span>
            <span className="dg-offer-arch">{u.archetype}</span>
            <span className="dg-offer-blurb">{u.blurb}</span>
          </button>
        ))}
      </div>
      <div className="dg-draft-foot">
        <button type="button" className="dg-skip" onClick={onSkip}>
          Take nothing <kbd>Space</kbd>
        </button>
        {/* The one moment a player definitely wants the reference is the
            moment they are being asked to choose. */}
        <span className="dg-draft-hint">
          <kbd>C</kbd> to read the whole pool
        </span>
      </div>
    </div>
  );
}

/**
 * What you are holding, and what it is doing.
 *
 * The draft was invisible for its entire first life: you picked four
 * cards over four minutes and then had no way to see them again. Worse,
 * the part that makes the draft a *build* — an upgrade reading stronger
 * because you also hold another one — happened silently in the modifier
 * pipeline, so the payoff for drafting coherently was real arithmetic
 * that never appeared on screen.
 *
 * So: four slots, always shown, because the empty ones teach the shape
 * of the run as much as the full ones do. A chip lights up when one of
 * its `holds` conditions is actually satisfied by something else in the
 * build — not when the two cards merely *suggest* each other, which is
 * a draft-weighting hint and not worth a player's attention mid-fight.
 */
function BuildStrip({ held }: { held: string[] }): JSX.Element {
  const slots = Array.from({ length: PICKS_PER_RUN }, (_, i) => held[i] ?? null);
  return (
    <div className="dg-build" aria-label="your build">
      <span className="dg-build-label">build</span>
      <span className="dg-build-slots">
        {slots.map((id, i) => {
          const u = id ? upgradeById(id) : null;
          if (!u) {
            return (
              <i
                key={`empty-${i}`}
                className="dg-build-slot"
                data-state="empty"
                title={`Draft ${i + 1} of ${PICKS_PER_RUN} — after runlevel ${i + 1} falls.`}
              />
            );
          }
          const links = activeLinks(u.id, held)
            .map((l) => upgradeById(l)?.name)
            .filter(Boolean);
          return (
            <span
              key={u.id}
              className="dg-build-slot"
              data-state="held"
              data-arch={u.archetype}
              data-linked={links.length > 0 ? "yes" : "no"}
              title={
                links.length > 0
                  ? `${u.name} — ${u.blurb}\n\nStronger because you hold: ${links.join(", ")}`
                  : `${u.name} — ${u.blurb}`
              }
            >
              <b>{u.name}</b>
              {links.length > 0 ? <i className="dg-build-link" aria-hidden="true" /> : null}
            </span>
          );
        })}
      </span>
    </div>
  );
}

/**
 * The intro, with a difficulty ladder on it.
 *
 * `nice` has eight levels and, until this existed, exactly one way to
 * reach any of them: hand-editing `?nice=` into the URL. Seven eighths
 * of the game's difficulty range was unreachable by anybody who had not
 * read the source — including the two levels the leaderboard's own copy
 * calls "kindest" and "cruellest".
 *
 * Offered only before a run starts, because changing it rebuilds the
 * run. That is the honest behaviour rather than a limitation: a seed
 * plays differently at every level, so switching mid-run would not be
 * the same fight continued, it would be a different one begun.
 */
function NicePlate({
  nice,
  lines,
  onPick,
}: {
  nice: NiceLevel;
  lines: string[];
  onPick: (n: NiceLevel) => void;
}): JSX.Element {
  return (
    <div className="dg-plate dg-plate-nice">
      <h2>./daemon</h2>
      {lines.map((l) => (
        <p key={l}>{l}</p>
      ))}

      <div className="dg-nice-pick">
        <span className="dg-nice-label" title="Your difficulty. Higher is kinder — a high nice value means a process is being considerate.">
          your nice · difficulty
        </span>
        <div className="dg-nice-row" role="group" aria-label="Difficulty">
          {NICE_CHOICES.map((n) => (
            <button
              key={n}
              type="button"
              className="dg-nice-b"
              data-on={n === nice ? "yes" : "no"}
              // The pointerdown handler on the stage starts the run, and
              // it ignores anything inside a button — so picking a
              // difficulty cannot also begin the fight on that level by
              // accident.
              onClick={() => onPick(n)}
              title={NICE_BLURB[n]}
            >
              {n > 0 ? `+${n}` : n}
            </button>
          ))}
        </div>
        <span className="dg-nice-why">{NICE_BLURB[nice]}</span>
      </div>

      <p className="dg-cta">Click the arena to begin</p>
    </div>
  );
}

function Plate({
  title,
  lines,
  cta,
}: {
  title: string;
  lines: string[];
  cta: string;
}): JSX.Element {
  return (
    <div className="dg-plate">
      <h2>{title}</h2>
      {lines.map((l) => (
        <p key={l}>{l}</p>
      ))}
      <p className="dg-cta">{cta}</p>
    </div>
  );
}


/**
 * The claim code, shown once.
 *
 * This is the piece that makes the whole identity design work on a
 * machine you do not own. The alternative — sign in, then play — fails
 * badly at exactly the venue this game is built for: at an event you
 * are sitting at somebody else's laptop with a queue behind you, and
 * signing in there means either typing a password into a stranger's
 * computer or having your run credited to whoever was here before you.
 *
 * So nothing is stored on this machine at all. The run posts
 * anonymously, stands on its own, and is verified the same as any
 * other; this code is the only thing you take away. Later, on your own
 * phone, one tap of Discord and this code moves the run to your name.
 *
 * It is shown once and is not retrievable, which is deliberate: a code
 * that can be re-requested for an existing run is a code that anyone
 * can request for somebody else's run.
 */
function ClaimCode({ code }: { code: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="dg-claim">
      <div className="dg-claim-head">
        <span className="dg-claim-label">claim code</span>
        <span className="dg-claim-note">shown once — put a name on this run later</span>
      </div>
      <div className="dg-claim-row">
        <code className="dg-claim-code">{code}</code>
        <button
          type="button"
          className="dg-claim-copy"
          onClick={() => {
            // Best effort. On an event machine the clipboard permission
            // may simply not be there, and a photograph of the screen
            // works just as well — which is why the code is short and
            // uses no ambiguous characters.
            void navigator.clipboard
              ?.writeText(code)
              .then(() => setCopied(true))
              .catch(() => setCopied(false));
          }}
        >
          {copied ? "copied" : "copy"}
        </button>
      </div>
      <p className="dg-claim-how">
        On your own phone, go to <b>daemon.skenmy.com/claim</b>, sign in with Discord and enter it.
        Nothing is stored on this computer.
      </p>
    </div>
  );
}

/**
 * Posting a win to the board.
 *
 * Three letters, because that is the arcade idiom and because it is the
 * smallest identity that needs no account, no email and no moderation
 * queue. The submission carries the replay and the initials and nothing
 * else — there is deliberately no field for a time, so a client cannot
 * claim one. The server re-simulates the stream and reads the result.
 */
function SubmitRow({
  replay,
  state,
  onSubmit,
}: {
  replay: string | null;
  state: { phase: "idle" | "sending" } | { phase: "done"; result: SubmitResult };
  onSubmit: (initials: string) => void;
}): JSX.Element {
  const [initials, setInitials] = useState("");
  const clean = initials.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3);

  if (state.phase === "done") {
    const r = state.result;
    return (
      <div className="dg-submitted">
        <div className="dg-submit" data-state={r.ok ? "ok" : "fail"} role="status">
          {r.ok ? (
            <span>
              {r.duplicate
                ? "already on the board — same run, same entry"
                : `verified by re-simulation · ${r.categories.join(" · ") || "any%"}`}
            </span>
          ) : (
            <span>{r.reason}</span>
          )}
        </div>
        {r.ok && r.claimCode ? <ClaimCode code={r.claimCode} /> : null}
      </div>
    );
  }

  // Encoding takes a moment. Saying so beats a dead control.
  const busy = state.phase === "sending" || replay === null;

  return (
    <form
      className="dg-submit"
      data-state="idle"
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy && clean) onSubmit(clean);
      }}
    >
      <label className="dg-submit-label" htmlFor="dg-initials">
        post to the board
      </label>
      <input
        id="dg-initials"
        className="dg-initials"
        value={clean}
        onChange={(e) => setInitials(e.target.value)}
        placeholder="AAA"
        maxLength={3}
        autoComplete="off"
        spellCheck={false}
        aria-label="Your initials, up to three characters"
      />
      <button className="dg-submit-go" type="submit" disabled={busy || !clean}>
        {replay === null ? "packing run…" : state.phase === "sending" ? "sending…" : "submit"}
      </button>
      <span className="dg-submit-note">the whole run goes, not a score</span>
    </form>
  );
}

function ResultsPlate({
  results,
  onAgain,
  onBoard,
  replay,
  submitState,
  onSubmit,
}: {
  results: Results;
  onAgain: () => void;
  onBoard: () => void;
  replay: string | null;
  submitState: { phase: "idle" | "sending" } | { phase: "done"; result: SubmitResult };
  onSubmit: (initials: string) => void;
}): JSX.Element {
  const ref = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    // Focus the button, so the one keypress that restarts has somewhere
    // to land whether the player reaches for the mouse or the keyboard.
    ref.current?.focus();
  }, []);
  const imp = results.record?.improvement ?? null;
  const pbs = imp?.personalBests ?? [];
  const goldCount = imp?.golds.length ?? 0;
  return (
    <div className="dg-plate dg-results" data-won={results.won ? "yes" : "no"}>
      <h2>{results.won ? "DAEMON terminated" : "Killed"}</h2>

      {/* The payoff. A PB is the only thing on this screen worth
        * shouting about, so it gets the top of the plate and the run
        * time is folded into it rather than repeated underneath. */}
      {pbs.length > 0 ? (
        <div className="dg-pb" role="status">
          <span className="dg-pb-flag">personal best</span>
          <span className="dg-pb-cats">
            {pbs.map((c) => categoryById(c)?.name ?? c).join(" · ")}
          </span>
          <span className="dg-pb-time">{formatTicks(results.ticks)}</span>
          {imp && imp.savedVsPb !== null ? (
            <span className="dg-pb-saved">{formatDelta(-imp.savedVsPb)} vs your old best</span>
          ) : (
            <span className="dg-pb-saved">first one on the board</span>
          )}
        </div>
      ) : (
        <div className="dg-results-time">{formatTicks(results.ticks)}</div>
      )}

      {results.rows.length > 0 ? (
        <table className="dg-results-splits">
          <caption>
            splits
            {results.pbTicks !== null ? ` — pb ${formatTicks(results.pbTicks)}` : " — no pb yet"}
          </caption>
          <tbody>
            {results.rows.map((r) => (
              <tr
                key={r.key}
                data-gold={r.gold ? "yes" : "no"}
                data-ahead={r.delta === null ? "none" : r.delta < 0 ? "yes" : "no"}
              >
                <th scope="row">
                  <span className="dg-split-phase">{r.phase}</span> {r.name}
                </th>
                <td className="dg-split-seg">{formatTicks(r.duration)}</td>
                <td className="dg-split-delta">
                  {r.delta === null ? "—" : formatDelta(r.delta)}
                </td>
                <td className="dg-split-at">{formatTicks(r.at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {results.record ? (
        <dl className="dg-results-grid dg-results-book">
          <div>
            <dt>golds</dt>
            <dd>
              {goldCount} / {results.rows.length}
            </dd>
          </div>
          <div>
            <dt>sum of best</dt>
            <dd>{results.sob === null ? "—" : formatTicks(results.sob)}</dd>
          </div>
          <div>
            <dt>runs</dt>
            <dd>{results.record.records.lifetime.runs}</dd>
          </div>
        </dl>
      ) : null}

      {/* Score gets its own line above the grid rather than a cell in
          it. On a losing run it is the only result there is — the run
          still counts, on one board — and burying it between "hits
          taken" and "damage dealt" would read as consolation. */}
      <div className="dg-results-score">
        <span className="dg-results-score-n">{results.score.toLocaleString()}</span>
        <span className="dg-results-score-l">
          points · peak load {(results.peakLoadMilli / 1000).toFixed(2)}
        </span>
      </div>

      <dl className="dg-results-grid">
        <div>
          <dt>grazes</dt>
          <dd>{results.grazes}</dd>
        </div>
        <div>
          <dt>hits taken</dt>
          <dd>{results.hitsTaken}</dd>
        </div>
        <div>
          <dt>cards cleared</dt>
          <dd>{results.cardsCleared}</dd>
        </div>
        <div>
          <dt>damage dealt</dt>
          <dd>{results.damageDealt}</dd>
        </div>
        <div>
          <dt>signals fired</dt>
          <dd>{results.signalsFired}</dd>
        </div>
      </dl>
      {results.won ? (
        <SubmitRow replay={replay} state={submitState} onSubmit={onSubmit} />
      ) : null}

      <div className="dg-results-actions">
        <button ref={ref} type="button" className="dg-again" onClick={onAgain}>
          Again <kbd>R</kbd>
        </button>
        {/* The moment anyone wants the board is the moment they have
            just finished a run and want to know what that was worth. */}
        <button type="button" className="dg-board-open" onClick={onBoard}>
          The board <kbd>L</kbd>
        </button>
      </div>
      <p className="dg-cta">
        No menu. One key. Go. <kbd>S</kbd> toggles live splits.
      </p>
    </div>
  );
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/** Release the pointer if we hold it. Safe to call at any time. */
function exitLock(): void {
  if (typeof document === "undefined") return;
  if (document.pointerLockElement) document.exitPointerLock();
}

export default DaemonGame;
